import React, { useEffect, useMemo, useState } from "react";

import { useGetCustomerQuery } from "../../api/customerApi";
import { LanguageCode } from "../../customers/customerlanguages";
import { TextControlPanel } from "../../machine-translation/TextControlPanel";
import {
  useGetDocumentSections,
  useGetDocumentStructures,
} from "../../planner/document-structure/manage/customhooks";
import { Spinner } from "../../utils/Spinner";
import { PREVIEW_LANGUAGE_CHANGE_EVENT } from "../../utils/partialUtils";
import { useGetPrompts, useGetTextBlocks } from "../copy-assistant/customHooks";
import { findConnectedChannelsForSection } from "../copy-assistant/utils";
import { ProductId } from "../product";
import { groupTextBlocksBySource } from "../textBlockGroups";
import { TranslationCard } from "./TranslationCard";

type Props = {
  productId: ProductId;
};

/**
 * The preview panel (product/preview.html) picks a default before the user
 * ever touches its dropdown -- mirror that same fallback order
 * (ProductPreviewView.get_language) so the tab doesn't flash a different
 * language while waiting for the panel's first sync event.
 */
function guessInitialLanguage(config: {
  text_preview_language?: LanguageCode | null;
  tag_input_language?: LanguageCode | null;
}): LanguageCode {
  return config.text_preview_language || config.tag_input_language || "en_US";
}

export const ProductTranslationsView: React.FC<Props> = ({ productId }) => {
  const { data: customer } = useGetCustomerQuery();
  const { data: textBlocks, isFetching, reFetch } = useGetTextBlocks(productId);
  const { data: sections } = useGetDocumentSections();
  const { data: structures } = useGetDocumentStructures();
  const { data: promptData } = useGetPrompts({ productId });

  // null until the preview panel reports its language; the fallback below fills
  // in until then. Deliberately not seeded via an effect -- doing so would also
  // re-fire whenever `customer` changes identity and clobber the user's choice.
  const [previewLanguage, setPreviewLanguage] = useState<LanguageCode | null>(
    null
  );

  useEffect(() => {
    const handleLanguageChange = (event: Event): void => {
      const { language } = (event as CustomEvent<{
        language: LanguageCode;
      }>).detail;
      setPreviewLanguage(language);
    };
    window.addEventListener(
      PREVIEW_LANGUAGE_CHANGE_EVENT,
      handleLanguageChange
    );
    return (): void =>
      window.removeEventListener(
        PREVIEW_LANGUAGE_CHANGE_EVENT,
        handleLanguageChange
      );
  }, []);

  const groups = useMemo(
    () => groupTextBlocksBySource(textBlocks.filter((block) => block.active)),
    [textBlocks]
  );

  const sectionNameById = new Map(
    sections.map((section) => [section.id, section.name])
  );
  const promptNameById = new Map(
    (promptData?.prompts ?? []).map((prompt) => [prompt.id, prompt.name])
  );

  if (isFetching || !customer) return <Spinner />;

  const activeLanguage =
    customer.getLanguageByCode(
      previewLanguage ?? guessInitialLanguage(customer.config)
    ) ?? undefined;

  return (
    <div className="tw-p-4" data-test="product-translations-view">
      <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-4">
        <div>
          <p className="tw-m-0 tw-text-lg tw-font-semibold tw-text-gray-900">
            Translations
          </p>
          <p className="tw-m-0 tw-text-sm tw-text-gray-500">
            Text blocks for this document. Edits save when you leave the field.
          </p>
        </div>
        <TextControlPanel layout="dropdown" />
      </div>

      {groups.length === 0 ? (
        <p className="tw-text-gray-500" data-test="no-text-blocks">
          This document has no text blocks yet. Generate text first and its
          translations will show up here.
        </p>
      ) : (
        groups.map((group) => (
          <TranslationCard
            key={group.source.id}
            productId={productId}
            group={group}
            sourceLanguage={customer.getLanguageByCode(group.source.language)}
            sectionName={
              group.source.section_id
                ? sectionNameById.get(group.source.section_id)
                : undefined
            }
            activeLanguage={activeLanguage}
            promptName={
              group.source.prompt_id
                ? promptNameById.get(group.source.prompt_id)
                : undefined
            }
            connectedChannelNames={
              group.source.section_id
                ? findConnectedChannelsForSection(
                    group.source.section_id,
                    customer,
                    structures
                  ).map(({ name }) => name)
                : []
            }
            onSaved={reFetch}
          />
        ))
      )}
    </div>
  );
};
